// Shared model catalog used by the HomeAI bar and the ai-route edge
// function. Prices are USD per 1M tokens (input+output blended).
// Cheapest first within each capability.
//
// Keep this in sync with supabase/functions/ai-route/index.ts.
export type Capability = "chat" | "image" | "research" | "analyze" | "build" | "agent";

export type ModelOption = {
  id: string;            // gateway model id
  label: string;         // short display name
  tier: "cheapest" | "balanced" | "premium";
  /** Blended $/1M tokens. */
  pricePer1M: number;
  blurb: string;
};

// Capability -> ordered list (cheapest first). Image/build/agent are
// fixed surfaces and not user-swappable yet.
export const MODELS_BY_CAPABILITY: Record<Capability, ModelOption[]> = {
  chat: [
    { id: "google/gemini-2.5-flash-lite",   label: "Gemini Flash Lite", tier: "cheapest", pricePer1M: 0.18, blurb: "Fastest & cheapest. Great for short answers." },
    { id: "google/gemini-3-flash-preview",  label: "Gemini 3 Flash",    tier: "balanced", pricePer1M: 0.45, blurb: "Best balance of speed, quality and cost." },
    { id: "openai/gpt-5-mini",              label: "GPT-5 mini",        tier: "balanced", pricePer1M: 0.90, blurb: "Strong general reasoning at low cost." },
    { id: "anthropic/claude-haiku-4-5",     label: "Claude Haiku 4.5",  tier: "balanced", pricePer1M: 1.20, blurb: "Fast Claude — great for tool-use loops." },
    { id: "openrouter/deepseek/deepseek-chat-v3.1", label: "DeepSeek V3.1", tier: "balanced", pricePer1M: 0.55, blurb: "Strong reasoning at very low cost." },
    { id: "openrouter/meta-llama/llama-3.3-70b-instruct", label: "Llama 3.3 70B", tier: "balanced", pricePer1M: 0.60, blurb: "Open weights, solid all-rounder." },
    { id: "anthropic/claude-sonnet-4-5",    label: "Claude Sonnet 4.5", tier: "premium",  pricePer1M: 3.00, blurb: "Best Claude for agentic loops." },
    { id: "openai/gpt-5",                   label: "GPT-5",             tier: "premium",  pricePer1M: 6.00, blurb: "Top-tier reasoning. Use for hard problems." },
    { id: "google/gemini-2.5-pro",          label: "Gemini 2.5 Pro",    tier: "premium",  pricePer1M: 4.20, blurb: "Best multimodal + long context reasoning." },
    { id: "anthropic/claude-opus-4",        label: "Claude Opus 4",     tier: "premium",  pricePer1M: 15.00, blurb: "Most thoughtful Claude — slow + deep." },
  ],
  analyze: [
    { id: "google/gemini-3-flash-preview",  label: "Gemini 3 Flash",    tier: "cheapest", pricePer1M: 0.45, blurb: "Fast structured analysis." },
    { id: "openai/gpt-5-mini",              label: "GPT-5 mini",        tier: "balanced", pricePer1M: 0.90, blurb: "Stronger tables and reasoning." },
    { id: "anthropic/claude-sonnet-4-5",    label: "Claude Sonnet 4.5", tier: "balanced", pricePer1M: 3.00, blurb: "Great for long docs + careful reasoning." },
    { id: "google/gemini-2.5-pro",          label: "Gemini 2.5 Pro",    tier: "premium",  pricePer1M: 4.20, blurb: "Large docs, deep analysis." },
    { id: "openai/gpt-5",                   label: "GPT-5",             tier: "premium",  pricePer1M: 6.00, blurb: "Top-tier reasoning over data." },
  ],
  research: [
    { id: "perplexity/sonar",               label: "Perplexity Sonar",       tier: "cheapest", pricePer1M: 1.00, blurb: "Live web search with citations." },
    { id: "perplexity/sonar-pro",           label: "Perplexity Sonar Pro",   tier: "balanced", pricePer1M: 3.00, blurb: "Deeper multi-step web research." },
    { id: "google/gemini-3-flash-preview",  label: "Gemini 3 Flash",    tier: "cheapest", pricePer1M: 0.45, blurb: "Quick synthesis from the model's knowledge." },
    { id: "perplexity/sonar-deep-research", label: "Sonar Deep Research", tier: "premium", pricePer1M: 8.00, blurb: "Expert deep-research report with many sources." },
    { id: "openai/gpt-5",                   label: "GPT-5",             tier: "premium",  pricePer1M: 6.00, blurb: "Most thorough, well-cited write-up." },
  ],
  image:    [{ id: "google/gemini-2.5-flash-image", label: "Nano Banana",        tier: "cheapest", pricePer1M: 0,    blurb: "Image generation (per-image pricing)." }],
  build:    [{ id: "lovable/ai-builder",            label: "Lovable App Builder",tier: "balanced", pricePer1M: 0,    blurb: "Scaffolds a working app." }],
  agent:    [{ id: "lovable/agent-runner",          label: "Agent Runner",        tier: "balanced", pricePer1M: 0,    blurb: "Multi-step tool-using agent." }],
};

export function defaultModelFor(cap: Capability): ModelOption {
  return MODELS_BY_CAPABILITY[cap][0];
}

export function findModel(id: string | null | undefined): ModelOption | null {
  if (!id) return null;
  for (const list of Object.values(MODELS_BY_CAPABILITY)) {
    const m = list.find((x) => x.id === id);
    if (m) return m;
  }
  return null;
}

export function formatPrice(usdPer1M: number): string {
  if (usdPer1M <= 0) return "—";
  if (usdPer1M < 1) return `$${usdPer1M.toFixed(2)}/1M tok`;
  return `$${usdPer1M.toFixed(2)}/1M tok`;
}